import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import EventEditModal from "../../components/EventEditModal";
import EventImagePositioner from "../EventImagePositioner";
import { formatSmartDate } from "../../utils/formatDate";
import { buildEventDetailsPath } from "../../utils/eventRoutes";
import MainPageTextEditor, {
  FormattedMainPageText,
  useMainPageText,
} from "../MainPageTextEditor";
import "./style.css";

const DEFAULT_TEXT = {
  description:
    "Participă la evenimente exclusive unde poți descoperi, testa și învăța despre puterea uleiurilor esențiale",
};

const TEXT_FIELDS = [
  { key: "description", label: "Descriere", rows: 4 },
];

const EventTypeBadge = ({ eventType }) => {
  const isOnline = eventType === "Online";
  return (
    <div className={`new-events-new__badge ${isOnline ? "new-events-new__badge--online" : ""}`}>
      <span className="new-events-new__badge-dot"></span>
      {isOnline ? "Eveniment Online" : "Eveniment fizic"}
    </div>
  );
};

const parseEndDate = (dateFrom, dateTo, dateStr) => {
  if (dateTo) return new Date(dateTo);
  if (dateFrom) return new Date(dateFrom);
  // Fallback: parse timeInterval string
  if (!dateStr) return null;
  const months = {
    ianuarie: 0, februarie: 1, martie: 2, aprilie: 3, mai: 4, iunie: 5,
    iulie: 6, august: 7, septembrie: 8, octombrie: 9, noiembrie: 10, decembrie: 11,
  };
  const cleaned = dateStr.trim().toLowerCase();
  const rangeMatch = cleaned.match(/(\d{1,2})\s*[-–]\s*(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (rangeMatch) {
    const m = months[rangeMatch[3]];
    if (m !== undefined) return new Date(parseInt(rangeMatch[4]), m, parseInt(rangeMatch[2]), 23, 59, 59);
  }
  const simpleMatch = cleaned.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (simpleMatch) {
    const m = months[simpleMatch[2]];
    if (m !== undefined) return new Date(parseInt(simpleMatch[3]), m, parseInt(simpleMatch[1]), 23, 59, 59);
  }
  return null;
};

const PloomEventNew = () => {
  const [nextEvent, setNextEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [content, setContent] = useMainPageText("events", DEFAULT_TEXT);
  const { user } = useSelector((store) => store);

  const isAdmin =
    user?.user?.email === process.env.REACT_APP_ADMIN ||
    user?.user?.email === process.env.REACT_APP_ARI_ADMIN;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/campaign/get`);
        const data = Array.isArray(res.data) ? res.data : [];
        const now = new Date();

        const upcoming = data
          .filter((e) => !e.skipped)
          .map((e) => {
            const dateStr = e.timeInterval ? e.timeInterval.split(",")[0]?.trim() : "";
            const endDate = parseEndDate(e.dateFrom || "", e.dateTo || "", dateStr);
            return {
              ...e,
              id: e.id || e.docId,
              title: e.title,
              location: e.location || "",
              date: dateStr,
              time: e.timeInterval ? e.timeInterval.split(",")[1]?.trim() : "",
              link: buildEventDetailsPath(e),
              dateFrom: e.dateFrom || "",
              dateTo: e.dateTo || "",
              eventType: e.eventType || "Fizic",
              coverImage: e.coverImage || "",
              imagePositionX: e.imagePositionX ?? 50,
              imagePositionY: e.imagePositionY ?? 50,
              imageZoom: e.imageZoom ?? 1,
              _endDate: endDate,
            };
          })
          .filter((e) => !e._endDate || e._endDate >= now)
          .sort((a, b) => {
            if (!a._endDate) return 1;
            if (!b._endDate) return -1;
            return a._endDate - b._endDate;
          });

        setNextEvent(upcoming[0] || null);
      } catch (err) {
        console.error("Error fetching events for homepage:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <section className="new-events-new main-page-text-editable">
      <MainPageTextEditor
        section="events"
        sectionLabel="Evenimente"
        fields={TEXT_FIELDS}
        value={content}
        onSaved={setContent}
      />
      <div className="new-events-new__inner">
        <div className="new-events-new__header">
          <span className="new-events-new__pre-title">EVENIMENTE</span>
          <h2 className="new-events-new__title">Întâlnește-ne</h2>
          <p className="new-events-new__subtitle">în persoană</p>
          <p className="new-events-new__desc">
            <FormattedMainPageText text={content.description} />
          </p>
        </div>

        {loading ? (
          <div className="new-events-new__loading">
            <div className="new-events-new__spinner"></div>
          </div>
        ) : !nextEvent ? (
          <p className="new-events-new__empty">
            Momentan nu sunt evenimente programate. Revino curând.
          </p>
        ) : (
          <div className="new-events-new__grid new-events-new__grid--single">
            <div className="new-events-new__card" style={{ position: "relative" }}>
              {/* Edit button — admin only */}
              {isAdmin && (
                <button
                  className="new-events-new__edit-btn"
                  onClick={() => setEditingEvent(nextEvent)}
                  title="Editează"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              )}

              {/* Cover photo */}
              <div className="new-events-new__card-photo">
                <EventImagePositioner
                  eventId={nextEvent.id}
                  src={nextEvent.coverImage}
                  alt={nextEvent.title}
                  initialX={nextEvent.imagePositionX ?? 50}
                  initialY={nextEvent.imagePositionY ?? 50}
                  initialZoom={nextEvent.imageZoom ?? 1}
                  isAdmin={isAdmin}
                />
                <div className="new-events-new__card-overlay">
                  <EventTypeBadge eventType={nextEvent.eventType} />
                  <div className="new-events-new__card-overlay-text">
                    <h3 className="new-events-new__card-title">{nextEvent.title}</h3>
                    {nextEvent.subtitle && (
                      <p className="new-events-new__card-subtitle">{nextEvent.subtitle}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Card body */}
              <div className="new-events-new__card-body">
                <div className="new-events-new__details">
                  <div className="new-events-new__detail">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>{nextEvent.location}</span>
                  </div>
                  <div className="new-events-new__detail">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>
                      {(nextEvent.dateFrom || nextEvent.dateTo)
                        ? formatSmartDate(nextEvent.dateFrom, nextEvent.dateTo)
                        : nextEvent.date}
                    </span>
                  </div>
                  {nextEvent.time && (
                    <div className="new-events-new__detail">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>{nextEvent.time}</span>
                    </div>
                  )}
                </div>
                <Link to={nextEvent.link} className="new-events-new__btn">
                  Rezervă Locul Tău
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="new-events-new__more">
          <Link to="/evenimente" className="new-events-new__more-link">
            Vezi toate evenimentele
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Edit Modal */}
        {editingEvent && (
          <EventEditModal
            event={editingEvent}
            onClose={() => setEditingEvent(null)}
            onSaved={() => window.location.reload()}
          />
        )}
      </div>
    </section>
  );
};

export default PloomEventNew;
